import { Loading, Panel } from "@appkit4/react-components";
import { max } from "dayjs";

import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AutoSizer, List } from "react-virtualized";
import { v4 as uuidv4 } from "uuid";
import CompanyPanel from "../../components/CompanyPanel";
import DataImportModal from "../../components/DataImportModal/DataImportModal";
import toast from "../../components/DismissibleToast";
import { BackButton, NextButton } from "../../components/ProgressNav";
import {
  Button,
  CalendarPicker,
  Input,
  Switch,
} from "../../components/ReadonlyAwareInputs";
import CustomTableHeader from "../../components/tableHeader/tableHeader";
import RoundedBorder from "../../components/ui/RoundedBorder";
import { AuthContext } from "../../services/AuthProvider";
import {
  get_company_id_by_name,
  get_group_companies,
} from "../../services/dredger/dredger.service";
import {
  canProgressCalculation,
  disableProgressInViewMode,
  getCurrentPeriodIsReadonly,
  getGroupData,
  getPageTooltips,
  getPeriodByIndex,
  getPoaIndex,
  getSelectedPeriod,
  getViewMode,
  setGroupData,
} from "../../services/GroupContext";
import sqlService from "../../services/sqldatabase/sqldatabase.service";
import { processDate, processDateJS } from "../../utils/dateProcessor";
import CompanyRefreshModal from "./CompanyRefreshModal";
import "./groupCompanies.scss";

const TAX_OFFICE_REGEX = /^\d{3}$/;
const UTR_REGEX = /^\d{5}-\d{5}$/;

const ukCompaniesMessages = {
  periodOneRolledForward:
    "UK Group Companies not previously reviewed. Period 1 values rolled forward.",
  priorPeriodRolledForward:
    "UK Group Companies not previously reviewed. Prior period status has been rolled forward.",
  noPriorInfoLocated:
    "UK Group Companies not previously reviewed. No prior period information located - by default, all group companies are assumed to be UK group companies. Please review this status- in particular the ultimate parent.",
  assumedAsUkCompanies:
    "UK Group Companies not previously reviewed. By default, all group companies are assumed to be UK group companies. Please review this status- in particular the ultimate parent.",
  onesourceCompaniesIdentified:
    "Companies have been identified from OneSource - UK Group Company status not reviewed.",
};

const GroupCompanies = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isPosting, setIsPosting] = useState(false);

  const [isError, setIsError] = useState(false);
  const [tableData, setTableData] = useState([]);

  const [dateLeftError, setDateLeftError] = useState([]);
  const [dateJoinedError, setDateJoinedError] = useState([]);

  const [createdCompanyIds, setCreatedCompanyIds] = useState([]);

  const [ukCompaniesInfoMessage, setUkCompaniesInfoMessage] = useState("");

  const [isFetchingOneSourceData, setIsFetchingOneSourceData] = useState(false);

  const changedCompanyIDs = useRef({});
  const newUkCompanyIDs = useRef([]);

  const auth = useContext(AuthContext);
  const [isReadOnly, setIsReadOnly] = useState();
  const selected_period = getSelectedPeriod();
  const {
    period_start: PoA_start_date,
    period_end: PoA_end_date,
    PoAPosition: PoA_Position,
  } = selected_period;
  const poaIndex = getPoaIndex();
  const isCurrentPeriodFirstPeriod = poaIndex === 0;
  const disableViewModeProgress = disableProgressInViewMode();
  const isClientFacing = useSelector((state) => state.usageInfo.isClientFacing);

  const tooltips = useMemo(() => getPageTooltips("groupCompanies"), []);

  const rowHeight = 55;
  const [listHeight, setListHeight] = useState(rowHeight);

  const showFirstNavigationMessage = async (savedData) => {
    setUkCompaniesInfoMessage(
      ukCompaniesMessages["onesourceCompaniesIdentified"],
    );
    const newSavedData = { ...savedData, wasOneSourceMessageShown: true };

    setIsPosting(true);

    const onSuccess = () => {
      setGroupData(newSavedData);
      setIsPosting(false);
    };
    const onError = () => setIsPosting(false);

    const result = sqlService.postResponse(newSavedData);
    return toast.save(result, onSuccess, onError);
  };

  useEffect(() => {
    setIsReadOnly(auth.isReadOnlyUser());

    const savedData = getGroupData();

    if (!savedData) {
      return;
    }

    const isOneSourceGroup = savedData?.group_id;
    const isGroupCompaniesProgressed = selected_period?.hasOwnProperty(
      "lastCompletedPoACalcs",
    );
    const wasOneSourceMessageShown =
      savedData?.wasOneSourceMessageShown ?? false;
    if (
      isOneSourceGroup &&
      !isGroupCompaniesProgressed &&
      !wasOneSourceMessageShown
    ) {
      showFirstNavigationMessage(savedData);
    }

    let ukCompanyIDs = getSelectedPeriod()?.ukCompanyIDs;

    if (!ukCompanyIDs) {
      ukCompanyIDs = initialiseUkCompanyIDs();
    }

    const companies = savedData.companies.map((c) => {
      const randomUuid = uuidv4();
      return {
        ...c,
        onesource_group_id: c.onesource_group_id,
        company_ID: c.company_ID ? c.company_ID : randomUuid,
        company_name: c.company_name ? c.company_name : undefined,
        tax_office: c.tax_office ? c.tax_office : undefined,
        utr: c.utr ? c.utr : undefined,
        date_join: c.date_join ? new Date(c.date_join) : undefined,
        date_left: c.date_left ? new Date(c.date_left) : undefined,
        from_onesource: c.hasOwnProperty("from_onesource")
          ? c.from_onesource
          : false,
        uk_group_company: ukCompanyIDs.includes(c.company_ID),
      };
    });

    let created_company_ids = selected_period?.created_company_ids;
    if (!created_company_ids) {
      created_company_ids = companies
        .filter((comp) => {
          return (
            processDateJS(comp?.date_join)?.isSameOrAfter(comp?.period_start) &&
            processDateJS(comp?.date_join)?.isSameOrBefore(comp?.period_end)
          );
        })
        .map((comp) => comp?.company_ID);

      if (isCurrentPeriodFirstPeriod) {
        created_company_ids.push(
          ...companies
            .filter((comp) => !comp?.date_join || !comp?.date_left)
            .map((comp) => comp?.company_ID),
        );
        created_company_ids = [...new Set(created_company_ids)];
      }
    }

    setTableData(companies);
    setCreatedCompanyIds(created_company_ids);
    const dateValidators = companies.map(() => false);
    setDateLeftError(dateValidators);
    setDateJoinedError(dateValidators);

    setIsLoading(false);
  }, []);

  const isCompanyCreatedInCurrentPeriod = (companyID) => {
    return createdCompanyIds?.includes(companyID);
  };

  const isEnabled = (company, dateType) => {
    const poaStart = processDateJS(PoA_start_date);
    const companyDate = processDateJS(company?.[dateType]);

    if (!companyDate) {
      if (dateType === "date_join")
        return isCompanyCreatedInCurrentPeriod(company?.company_ID);
      else if (dateType === "date_left") return true;
    }

    return (
      isCompanyCreatedInCurrentPeriod(company?.company_ID) ||
      companyDate.isSameOrAfter(poaStart)
    );
  };

  const handleDateJoinError = (
    index,
    value,
    type = "change",
    newlyAddedData = undefined,
  ) => {
    const tmpDateJoinedError = [...dateJoinedError];
    const tmpDateLeftError = [...dateLeftError];

    const values = Array.isArray(value) ? value : [value];
    const indexes = Array.isArray(index) ? index : [index];
    const companiesData = type === "change" ? tableData : newlyAddedData;

    values.forEach((value, i) => {
      const companyData =
        type === "change" ? companiesData[indexes[i]] : companiesData[i];

      const dateLeft = companyData["date_left"];

      if (!value && companyData["new"]) {
        if (!isCurrentPeriodFirstPeriod) {
          tmpDateJoinedError[indexes[i]] = true;
        }
      } else {
        tmpDateJoinedError[indexes[i]] =
          value &&
          ((processDateJS(value).isBefore(processDateJS(PoA_start_date)) &&
            !isCurrentPeriodFirstPeriod) ||
            (dateLeft &&
              processDateJS(value).isAfter(processDateJS(dateLeft))));

        if (dateLeft) {
          tmpDateLeftError[indexes[i]] =
            processDateJS(value)?.isAfter(processDateJS(dateLeft)) ||
            processDateJS(dateLeft)?.isBefore(processDateJS(PoA_start_date));
        }
      }
    });

    setDateJoinedError(tmpDateJoinedError);
    setDateLeftError(tmpDateLeftError);
  };

  const handleDateLeftError = (
    index,
    value,
    type = "change",
    newlyAddedData = undefined,
  ) => {
    const tmpDateLeftError = [...dateLeftError];
    const tmpDateJoinedError = [...dateJoinedError];

    const values = Array.isArray(value) ? value : [value];
    const indexes = Array.isArray(index) ? index : [index];
    const companiesData = type === "change" ? tableData : newlyAddedData;

    values.forEach((value, i) => {
      const companyData =
        type === "change" ? companiesData[indexes[i]] : companiesData[i];

      const dateJoin = companyData["date_join"];

      tmpDateLeftError[indexes[i]] =
        value &&
        ((processDateJS(value).isBefore(processDateJS(PoA_start_date)) &&
          !isCurrentPeriodFirstPeriod) ||
          (dateJoin && processDateJS(value).isBefore(processDateJS(dateJoin))));

      if (dateJoin) {
        tmpDateJoinedError[indexes[i]] =
          processDateJS(value)?.isBefore(processDateJS(dateJoin)) ||
          processDateJS(dateJoin)?.isBefore(processDateJS(PoA_start_date));
      }
    });

    setDateLeftError(tmpDateLeftError);
    setDateJoinedError(tmpDateJoinedError);
  };

  const is_duplicate_utr = (utr) => {
    const matches = tableData.filter(
      (row) => row.uk_group_company && row.utr === utr,
    );
    return matches.length > 1;
  };

  const hasTableError = useMemo(() => {
    return tableData.some((row, index) => {
      if (!row.company_name) return true;
      if (row.tax_office && !String(row.tax_office).match(TAX_OFFICE_REGEX))
        return true;
      if (row.utr && !row.utr.match(UTR_REGEX)) return true;
      if (row.uk_group_company && (!row.utr || !row.tax_office)) return true;
      if (is_duplicate_utr(row.utr)) return true;
      if (
        isEnabled(row, "date_join") &&
        !row.date_join &&
        !isCurrentPeriodFirstPeriod
      )
        return true;
      return false;
    });
  }, [tableData]);

  useEffect(() => {
    setIsError(
      dateJoinedError.some((err) => err) ||
        dateLeftError.some((err) => err) ||
        hasTableError,
    );

    setListHeight(
      Math.min(tableData.length * rowHeight, window.innerHeight * 0.78),
    );
  }, [tableData]);

  const isUltimateParent = (companyID) => {
    return getGroupData().ult_parent_ID === companyID;
  };

  const addCompanyToFirstPoA = (companyID) => {
    if (isCurrentPeriodFirstPeriod) {
      setCreatedCompanyIds((ids) => [...ids, companyID]);
    }
  };

  const tableChange = (index, value, name) => {
    const rowsInput = [...tableData];
    if (name === "date_join" || name === "date_left") {
      // on deleting date_join in the first period, add the company_ID to the first PoA
      if (name === "date_join" && !value)
        addCompanyToFirstPoA(rowsInput[index]["company_ID"]);
      value = processDate(value);

      if (name === "date_join") {
        handleDateJoinError(index, value);
      } else if (name === "date_left") {
        handleDateLeftError(index, value);
      }
    }

    if (name === "uk_group_company" && rowsInput[index]["new"]) {
      const company_ID = rowsInput[index]["company_ID"];
      if (value) {
        newUkCompanyIDs.current.push(company_ID);
      } else {
        newUkCompanyIDs.current = newUkCompanyIDs.current.filter(
          (id) => id !== company_ID,
        );
      }
    }

    rowsInput[index][name] = value;
    setTableData(rowsInput);
  };

  const getUtrFormat = (utr) => {
    if (!utr) return utr;
    return utr?.slice(0, 5) + "-" + utr?.slice(5);
  };

  const addTableRows = (newEntries = [{}]) => {
    const newCompanyIds = [];
    const dateLeftErrors = [];
    const dateJoinedErrors = [];

    const newRows = newEntries.map((newCompany) => {
      const newCompanyId = uuidv4();
      newCompanyIds.push(newCompanyId);
      dateLeftErrors.push(false);
      dateJoinedErrors.push(
        !newCompany?.date_join && !isCurrentPeriodFirstPeriod,
      );

      return {
        onesource_group_id: undefined,
        company_ID: newCompanyId,
        company_name: newCompany?.company_name ?? "",
        tax_office: newCompany?.tax_office?.toString() ?? "",
        utr: getUtrFormat(newCompany?.utr?.toString()) ?? "",
        date_join: newCompany?.date_join,
        date_left: newCompany?.date_left,
        from_onesource: false,
        uk_group_company: true,
        new: true,
      };
    });

    setTableData((prev) => [...prev, ...newRows]);

    setCreatedCompanyIds((ids) => [...ids, ...newCompanyIds]);
    newUkCompanyIDs.current.push(...newCompanyIds);
    setDateLeftError((prev) => [...prev, ...dateLeftErrors]);
    setDateJoinedError((prev) => [...prev, ...dateJoinedErrors]);
  };

  const deleteTableRows = (e, index) => {
    const rows = [...tableData];
    const deletedRowId = rows[index]?.company_ID;
    rows.splice(index, 1);
    newUkCompanyIDs.current = newUkCompanyIDs.current.filter(
      (id) => id !== deletedRowId,
    );
    setCreatedCompanyIds((ids) => ids.filter((id) => id !== deletedRowId));

    setTableData(rows);

    const dateJoinedValidators = [...dateJoinedError];
    dateJoinedValidators.splice(index, 1);
    setDateJoinedError(dateJoinedValidators);

    const dateLeftValidators = [...dateLeftError];
    dateLeftValidators.splice(index, 1);
    setDateLeftError(dateLeftValidators);
  };

  const replaceIdWithOnesourceId = (
    saved_data,
    nonOneSourceCompanyID,
    newOneSourceID,
  ) => {
    if (selected_period.created_company_ids?.includes(nonOneSourceCompanyID)) {
      setCreatedCompanyIds((ids) => [
        ...ids.filter((id) => id !== nonOneSourceCompanyID),
        newOneSourceID,
      ]);
    } else {
      const companyCreationPoAIndex = saved_data.periods_of_account?.findIndex(
        (poa) => {
          return poa.created_company_ids.includes(nonOneSourceCompanyID);
        },
      );

      if (companyCreationPoAIndex !== -1) {
        saved_data.periods_of_account[
          companyCreationPoAIndex
        ].created_company_ids = [
          ...saved_data.periods_of_account[
            companyCreationPoAIndex
          ].created_company_ids?.filter((id) => id !== nonOneSourceCompanyID),
          newOneSourceID,
        ];
      }
    }
  };

  const isCompanyOutsidePoA = (company, period) => {
    if (!company.date_join && !company.date_left) return false;

    return (
      processDateJS(company.date_left)?.isBefore(
        processDateJS(period.period_start),
      ) ||
      processDateJS(company.date_join)?.isAfter(
        processDateJS(period.period_end),
      )
    );
  };

  const initialiseUkCompanyIDs = () => {
    const savedData = getGroupData();
    let ukCompanyIDs;
    let otherPoa;

    if (poaIndex === 0) {
      otherPoa = undefined;
    } else {
      otherPoa = getPeriodByIndex(poaIndex - 1);
    }

    const otherPoaUkCompanyIDs = otherPoa?.ukCompanyIDs?.filter((id) => {
      const company = savedData?.companies?.find(
        (company) => company.company_ID === id,
      );
      return !isCompanyOutsidePoA(company, selected_period);
    });

    const companiesInsidePoA = savedData?.companies?.filter((company) => {
      return !isCompanyOutsidePoA(company, selected_period);
    });

    const hasUkGroupCompanyInCompaniesList = savedData?.companies?.some(
      (company) => company.hasOwnProperty("uk_group_company"),
    );

    if (otherPoaUkCompanyIDs) {
      ukCompanyIDs = otherPoaUkCompanyIDs;
      setUkCompaniesInfoMessage(
        ukCompaniesMessages["priorPeriodRolledForward"],
      );
    } else if (hasUkGroupCompanyInCompaniesList) {
      const companytableUKCompanyIDs = companiesInsidePoA
        ?.filter(({ uk_group_company }) => uk_group_company)
        ?.map(({ company_ID }) => company_ID);

      ukCompanyIDs = companytableUKCompanyIDs;

      if (otherPoa)
        setUkCompaniesInfoMessage(
          ukCompaniesMessages["periodOneRolledForward"],
        );
    } else {
      const allcompaniesUKCompanyIDs = companiesInsidePoA?.map(
        ({ company_ID }) => company_ID,
      );
      ukCompanyIDs = allcompaniesUKCompanyIDs;

      if (otherPoa) {
        setUkCompaniesInfoMessage(ukCompaniesMessages["noPriorInfoLocated"]);
      } else {
        setUkCompaniesInfoMessage(ukCompaniesMessages["assumedAsUkCompanies"]);
      }
    }

    return ukCompanyIDs;
  };

  const save = () => {
    const isViewModeOn = getViewMode();
    if (!tableData || tableData.length === 0) {
      throw new Error("You must provide at least one company...");
    }

    if (hasTableError) {
      throw new Error("There are some required fields missing...");
    }

    // TODO check if parent name has changed
    setIsPosting(true);

    let savedData = getGroupData();
    const companies = tableData.map((row) => {
      return {
        onesource_group_id: row.onesource_group_id,
        company_ID: row.company_ID,
        company_name: row.company_name,
        from_onesource: row?.from_onesource || false,
        tax_office: row.tax_office,
        utr: row.utr,
        uk_group_company: row.uk_group_company,
        date_join: row.date_join,
        date_left: row.date_left,
      };
    });

    const ukCompanyIDs = companies
      ?.filter(({ uk_group_company }) => uk_group_company)
      ?.map(({ company_ID }) => company_ID);

    const onSuccess = () => {
      setGroupData(savedData);
      setIsPosting(false);
    };

    const onError = () => setIsPosting(false);

    if (isReadOnly || getCurrentPeriodIsReadonly()) {
      const currentPoA = savedData.periods_of_account[poaIndex];
      const ukCompaniesExist = currentPoA?.ukCompanyIDs;
      if (ukCompaniesExist) return;

      savedData.periods_of_account[poaIndex] = { ...currentPoA, ukCompanyIDs };

      const result = sqlService.postResponse(savedData);
      return toast.save(result, onSuccess, onError);
    }

    const toggledToOneSourceKeys = Object.keys(changedCompanyIDs.current);

    if (toggledToOneSourceKeys.length > 0) {
      savedData.company_accounting_periods =
        savedData.company_accounting_periods?.map((ap) => {
          return {
            ...ap,
            company_ID: toggledToOneSourceKeys.includes(ap.company_ID)
              ? changedCompanyIDs.current[ap.company_ID]
              : ap.company_ID,
          };
        });
    }

    // filter caps in case a company has been deleted.
    const caps = savedData.company_accounting_periods
      ? savedData.company_accounting_periods.filter(
          (cap) =>
            companies.findIndex((comp) => comp.company_ID === cap.company_ID) >
            -1,
        )
      : [];

    if (isCurrentPeriodFirstPeriod) {
      savedData.periods_of_account = savedData.periods_of_account.map(
        (poa, index) => {
          if (index === 0) return poa;
          return {
            ...poa,
            created_company_ids: poa?.created_company_ids?.filter(
              (id) => !createdCompanyIds.includes(id),
            ),
          };
        },
      );
    }

    if (toggledToOneSourceKeys.length > 0) {
      savedData.periods_of_account = savedData.periods_of_account.map((poa) => {
        return {
          ...poa,
          adjusted_caps: poa?.adjusted_caps?.map((adj_cap) => {
            if (toggledToOneSourceKeys.includes(adj_cap.company_ID)) {
              return {
                ...adj_cap,
                company_ID: changedCompanyIDs.current[adj_cap.company_ID],
              };
            }
            return adj_cap;
          }),
        };
      });
    }

    savedData.periods_of_account?.slice(poaIndex + 1).forEach((period) => {
      if (period?.ukCompanyIDs) {
        const includedIDs = newUkCompanyIDs.current.filter((id) => {
          const company = tableData.find(
            (company) => company.company_ID === id,
          );
          return !isCompanyOutsidePoA(company, period);
        });

        period.ukCompanyIDs = [...period.ukCompanyIDs, ...includedIDs];
      }
    });

    const canProgressCalc = canProgressCalculation();

    savedData.periods_of_account[poaIndex] = {
      ...savedData.periods_of_account[poaIndex],
      ...canProgressCalc,
      created_company_ids: [...createdCompanyIds],
      ukCompanyIDs,
    };

    savedData = {
      ...savedData,
      companies: companies?.map(({ uk_group_company, ...rest }) => rest),
      company_accounting_periods: caps,
    };

    const result = sqlService.postResponse(savedData);
    return toast.save(result, onSuccess, onError);
  };

  const [is_refreshing, set_is_refreshing] = useState(false);
  const [show_new_company_modal, set_show_new_company_modal] = useState(false);

  // Companies found in OneSource but don't exist locally
  const [new_companies, set_new_companies] = useState([]);

  // Companies found locally but doesn't exist in OneSource
  const [missing_companies, set_missing_companies] = useState([]);

  const refresh_from_onesource = async () => {
    const group_data = getGroupData();
    if (!group_data) {
      return;
    }

    const is_onesource_group = Number.isFinite(
      Number(group_data.group_id ?? undefined),
    ); // Only OneSource groups have a group_id that is a number. isFinite checks if it is a numebr
    if (!is_onesource_group) {
      toast.error("Refresh unsuccesful - group not linked to OneSource");
      return;
    }

    set_is_refreshing(true);

    try {
      const userEmail = auth.getEmail();
      const onesource_companies = await get_group_companies(
        Number(group_data.group_id),
        userEmail,
      );
      const current_companies = tableData.map((c) => c.company_ID);

      // Get a list of companies that were found in OneSource but do not exist locally
      const new_companies = onesource_companies.filter((new_company) => {
        return !current_companies.includes(new_company.company_ID);
      });

      // Get a list of companies that exist locally but not found in OneSource
      const missing_companies = tableData.filter((existing_company) => {
        return !onesource_companies.find(
          (c) => c.company_ID === existing_company.company_ID,
        );
      });

      if (new_companies.length === 0 && missing_companies.length === 0) {
        toast.success("No new companies were found in OneSource");
        set_is_refreshing(false);
        return;
      }

      set_new_companies(new_companies);
      set_missing_companies(missing_companies);
      set_show_new_company_modal(true);
    } catch (error) {
      console.error(error);
      toast.error(
        "Something went wrong when fetching from OneSource. Please try again!",
      );
    }

    set_is_refreshing(false);
  };

  const rowRenderer = ({ index, key, style }) => {
    const data2 = tableData[index];
    return (
      <tr
        style={{ ...style }}
        key={index}
        className={isClientFacing ? "grid-table-no-onsource" : "grid-table"}
      >
        <td
          style={{
            verticalAlign:
              dateLeftError && dateLeftError[index] ? "middle" : "top",
          }}
          key={1}
        >
          <Input
            className="col"
            value={data2["company_name"]}
            onChange={(evnt) => {
              tableChange(index, evnt, "company_name");
            }}
            error={!data2["company_name"]}
          />
        </td>
        <td
          key={8}
          style={{
            verticalAlign:
              dateLeftError && dateLeftError[index] ? "middle" : "top",
            paddingTop: dateLeftError && dateLeftError[index] ? "" : "0.9rem",
          }}
        >
          <Switch
            style={{ alignItems: "center", justifyContent: "center" }}
            defaultChecked={data2["uk_group_company"]}
            checked={data2["uk_group_company"]}
            onChange={(evnt) => {
              tableChange(index, evnt, "uk_group_company");
            }}
          />
        </td>
        {!isClientFacing && (
          <td
            key={2}
            style={{
              verticalAlign:
                dateLeftError && dateLeftError[index] ? "middle" : "top",
              paddingTop: dateLeftError && dateLeftError[index] ? "" : "0.9rem",
            }}
          >
            <Switch
              style={{ alignItems: "center", justifyContent: "center" }}
              defaultChecked={false}
              checked={Boolean(data2["from_onesource"])}
              disabled={isFetchingOneSourceData}
              onChange={async (value) => {
                if (!value) {
                  tableChange(index, value, "from_onesource");
                  return;
                }

                const name = data2?.company_name;
                if (!name) {
                  toast.error(
                    "Please enter a Company Name to search OneSource",
                  );
                  return;
                }

                setIsFetchingOneSourceData(true);

                const userEmail = auth.getEmail();
                const nonOneSourceCompanyID = data2?.company_ID;
                const promise = get_company_id_by_name(name, userEmail);

                const onSuccess = (company) => {
                  const saved_data = getGroupData();
                  if (!saved_data) {
                    toast.error("Failed to fetch saved data...");
                    return;
                  }

                  const updatedCompany = { ...data2, ...company };
                  saved_data.companies[index] = updatedCompany;

                  if (nonOneSourceCompanyID !== updatedCompany.company_ID) {
                    replaceIdWithOnesourceId(
                      saved_data,
                      nonOneSourceCompanyID,
                      updatedCompany.company_ID,
                    );
                    changedCompanyIDs.current[nonOneSourceCompanyID] =
                      updatedCompany.company_ID;
                  }
                  setGroupData(saved_data);

                  const new_table_data = tableData;
                  new_table_data[index] = updatedCompany;
                  setTableData(new_table_data);
                  tableChange(index, value, "from_onesource");
                  setIsFetchingOneSourceData(false);
                };

                toast.save(
                  promise,
                  onSuccess,
                  () => setIsFetchingOneSourceData(false),
                  {
                    loading: "Fetching data from OneSource...",
                    success: "Found data in OneSource",
                    error: "Failed to fetch data from OneSource",
                  },
                  3000,
                );
              }}
            />
          </td>
        )}
        <td
          key={3}
          style={{
            verticalAlign:
              dateLeftError && dateLeftError[index] ? "middle" : "top",
          }}
        >
          <Input
            className="col"
            value={data2["tax_office"]}
            error={
              (!data2["tax_office"] && data2["uk_group_company"]) ||
              (data2["tax_office"] && !data2["tax_office"].match(/^\d{3}$/))
            }
            onChange={(value) => {
              if (value.length <= 3) tableChange(index, value, "tax_office");
            }}
            placeholder={"xxx"}
            required={data2["uk_group_company"]}
          />
        </td>
        <td
          key={4}
          style={{
            verticalAlign:
              dateLeftError && dateLeftError[index] ? "middle" : "top",
          }}
        >
          <Input
            className="col"
            value={data2["utr"]}
            onChange={(value) => {
              if (value.length > 11) return;

              if (value.match(/^\d{5}-\d{0,5}$/) || value.match(/^\d{0,5}$/)) {
                // correct format
                tableChange(index, value, "utr");
              } else if (value.match(/^\d{0,}-?\d{0,}$/)) {
                // dash missing or in wrong place
                const removeDash = value.replace("-", "");
                const firstDigits = removeDash.substring(0, 5);
                const laterDigits = removeDash.substring(5);
                tableChange(index, firstDigits + "-" + laterDigits, "utr");
              } else {
                tableChange(index, "", "utr");
              }
            }}
            error={
              (!data2["utr"] && data2["uk_group_company"]) ||
              (data2["utr"] &&
                (!data2["utr"].match(/^\d{5}-\d{5}$/) ||
                  is_duplicate_utr(data2["utr"])))
            }
            errorNode={is_duplicate_utr() && "Duplicate UTR"}
            required={data2["uk_group_company"]}
            placeholder={"xxxxx-xxxxx"}
          ></Input>
        </td>
        <td
          key={5}
          style={{
            verticalAlign:
              dateLeftError && dateLeftError[index] ? "middle" : "top",
          }}
        >
          <CalendarPicker
            className="col"
            minDate={
              isCurrentPeriodFirstPeriod
                ? undefined
                : processDateJS(PoA_start_date)
            }
            fieldWidth="100%"
            disabled={!dateJoinedError[index] && !isEnabled(data2, "date_join")}
            value={data2?.date_join}
            format="DD/MM/YYYY"
            placeholder="dd/mm/yyyy"
            error={dateJoinedError[index]}
            useCustomValidation
            customErrorNode="Must be after PoA start and not after Date Left"
            onChange={(evnt) => {
              tableChange(index, evnt, "date_join");
            }}
          />
        </td>

        <td
          key={6}
          style={{
            verticalAlign:
              dateLeftError && dateLeftError[index] ? "middle" : "top",
          }}
        >
          <CalendarPicker
            className="col"
            fieldWidth="100%"
            value={data2?.date_left}
            format="DD/MM/YYYY"
            placeholder="dd/mm/yyyy"
            onChange={(evnt) => {
              tableChange(index, evnt, "date_left");
            }}
            customErrorNode="Must be after PoA start and not before Date Joined"
            minDate={max(
              processDateJS(PoA_start_date)?.add(1, "day"),
              processDateJS(data2?.date_join)?.add(1, "day"),
            )}
            disabled={!dateLeftError[index] && !isEnabled(data2, "date_left")}
            error={dateLeftError[index]}
            useCustomValidation
          />
        </td>

        {/* joined before the period ended && left after the period started */}
        <td
          key={7}
          style={{
            verticalAlign:
              dateLeftError && dateLeftError[index] ? "middle" : "top",
          }}
        >
          <div
            style={{
              width: "30px",
              height: "30px",
              borderRadius: "50%",
              margin: "auto",
              ...((data2?.date_join === undefined ||
                data2?.date_join <= new Date(selected_period.period_end)) &&
              (data2?.date_left === undefined ||
                data2?.date_left >= new Date(selected_period.period_start))
                ? { backgroundColor: "#87e964" }
                : { backgroundColor: "#ff6666" }),
            }}
          ></div>
        </td>
        <td
          style={{
            verticalAlign:
              dateLeftError && dateLeftError[index] ? "middle" : "top",
            paddingTop: dateLeftError && dateLeftError[index] ? "" : "0.3rem",
          }}
        >
          <Button
            className="btn btn-outline-danger"
            onClick={(e) => deleteTableRows(e, index)}
            disabled={
              !isEnabled(data2, "date_join") ||
              isUltimateParent(data2.company_ID)
            }
            kind="secondary"
          >
            <span className="Appkit4-icon icon-close-outline"></span>
          </Button>
        </td>
      </tr>
    );
  };

  if (isLoading) {
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      ></Loading>
    );
  }

  return (
    <div className="ap-container">
      <CompanyPanel />

      <Panel className="mt-4" style={{ paddingTop: 10 }}>
        <h2>Group companies during the selected period of account</h2>

        <p>
          The list below should include all companies within the Worldwide Group
          which are UK Group Companies. It should also include the UPE, even if
          this is not a UK Group Company.
        </p>
        {!isClientFacing && (
          <>
            <p>
              Where the Group has been linked to ONESOURCE, the companies should
              have been identified as belonging to the group, and the list
              should be pre-populated accordingly. Please review the list and
              make any necessary adjustments. If group companies are on
              ONESOURCE but are missing from the list, please get in touch with
              us at{" "}
              <a
                href="mailto:uk_cir_app_support@pwc.com?subject=ONESOURCE linking support"
                target="_blank"
                rel="noreferrer"
              >
                uk_cir_app_support@pwc.com
              </a>
              .
            </p>
            <p>
              Where the group is not linked to ONESOURCE, group companies can be
              added manually (using the + button).
            </p>
          </>
        )}
        {isClientFacing && (
          <p>
            Additional group companies can be added manually (using the +
            button).
          </p>
        )}
        <p>
          Where a UK company has joined (e.g. by acquisition or incorporation)
          or left (e.g. by sale or elimination) the group in the period, this
          information must be entered, but the company should still be a UK
          group company for the period of joining / leaving. See tooltips on
          these columns for further details.
        </p>
        <p>
          It is only necessary to include UK Group Companies (any company which
          is a member of the group and within the charge to UK corporation tax
          at some point in the period) within this list. Whilst other entities
          (e.g. overseas companies, dormant entities, etc) can be added if
          desired, they must be reflected as being non-UK Group Companies using
          the toggle.
        </p>
        <p className="error">{ukCompaniesInfoMessage}</p>

        {/* Virtualized Table with Rounded Borders */}
        <RoundedBorder>
          {/* Table Header */}
          <table style={{ width: "100%" }} className="table">
            <thead>
              <tr
                className={
                  isClientFacing ? "grid-table-no-onsource" : "grid-table"
                }
              >
                <CustomTableHeader
                  title={
                    <span>
                      Company Name<span style={{ color: "white" }}></span>
                    </span>
                  }
                  required={true}
                  tooltipText={tooltips.companyName}
                  key={1}
                />

                <CustomTableHeader
                  title="UK Group Company?"
                  required={false}
                  tooltipText={tooltips.ukGroupCompany}
                  key={8}
                />

                {!isClientFacing && (
                  <CustomTableHeader
                    title="From OneSource?"
                    required={false}
                    tooltipText={tooltips.fromOneSource}
                    key={2}
                  />
                )}

                <CustomTableHeader
                  title="Tax office"
                  required={false}
                  tooltipText={tooltips.taxOffice}
                  key={3}
                />

                <CustomTableHeader
                  title="UTR"
                  required={false}
                  tooltipText={tooltips.UTR}
                  key={4}
                />

                <CustomTableHeader
                  title="Date joined (if during PoA)"
                  required={false}
                  tooltipText={tooltips.dateJoined}
                  key={5}
                />

                <CustomTableHeader
                  title="Date left (if during PoA)"
                  required={false}
                  tooltipText={tooltips.dateLeft}
                  key={6}
                />

                <CustomTableHeader
                  title="Present in period"
                  required={false}
                  tooltipText={tooltips.presentInPeriod}
                  key={7}
                />

                <th>
                  <Button
                    add
                    disabled={isError}
                    className="btn btn-outline-success"
                    onClick={() => addTableRows()}
                  >
                    <span className="Appkit4-icon icon-plus-outline"></span>
                  </Button>
                </th>
              </tr>
            </thead>
          </table>

          {/* Virtualized List */}
          <div style={{ width: "100%", height: listHeight }}>
            <AutoSizer disableHeight>
              {({ width }) => (
                <List
                  width={width}
                  height={listHeight}
                  rowCount={tableData.length}
                  rowHeight={55}
                  rowRenderer={rowRenderer}
                  overscanRowCount={5}
                />
              )}
            </AutoSizer>
          </div>
        </RoundedBorder>
        <div className="d-flex justify-content-start mt-4 gap-2">
          {!isClientFacing && (
            <Button
              className="w-12"
              disabled={is_refreshing}
              onClick={refresh_from_onesource}
            >
              {is_refreshing ? (
                <Loading
                  loadingType={"circular"}
                  circularWidth={25}
                  indeterminate
                />
              ) : (
                "Refresh from OneSource"
              )}
            </Button>
          )}
          <DataImportModal
            pageName="groupCompanies"
            data={tableData}
            setData={addTableRows}
            isPageLoading={isLoading}
          />

          <CompanyRefreshModal
            visible={show_new_company_modal}
            set_visible={set_show_new_company_modal}
            new_companies={new_companies}
            missing_companies={missing_companies}
            handle_submit={(selected_companies) => {
              const group_data = getGroupData();
              if (!group_data) {
                toast.error("Failed to save the new companies!");
                return;
              }

              const companies = [...tableData, ...selected_companies];
              const new_group_data = { ...group_data, companies: companies };
              setGroupData(new_group_data);
              setTableData(companies);
              set_show_new_company_modal(false);
            }}
          ></CompanyRefreshModal>
        </div>
      </Panel>

      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <BackButton disabled={isPosting} />
          </div>
          <div className="d-flex gap-2">
            <Button
              hiddenInReadonly
              disabled={isError}
              loading={isPosting}
              onClick={save}
              kind="secondary"
            >
              Save
            </Button>
            <NextButton
              disabled={isError || disableViewModeProgress}
              loading={isPosting}
              preNavigation={save}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupCompanies;
